import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
    useCurrentWorkspace,
    useCurrentWorkflow,
    useCurrentWorkspaceApiKey,
    useCreateRapidWorkflow,
    useIsWorkflowCreating,
    useSetWorkflow,
    useSetCreatingRapidWorkflow,
    useFetchCurrentWorkspaceApiKey,
    useCurrentRapidData,
    useInitializeModelFromRapidData,
    useClearModelState,
    useUser
} from "@/store";
import useApi from "@/hooks/useApi";
import { ModelStatus } from "@/components/flow/shared/ModelStatus/cms";
import { DataMode } from "@/components/flow/shared/Deploy/documentation/types";
import { projectSelector } from "@rflocal/shared";
import {
    AvailableRapidModelArchitectures,
    RapidTrainStatus
} from "../components/flow/shared/Deploy/documentationCms";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { DeployContext, DeployContextType } from "./DeployContext";
import {
    CreateRapidWorkflowProps,
    BuildMetadata,
    BuildInferenceType
} from "@/store/slices/workflowSlice";
import { useMedia } from "./MediaContext";
import { useVisibilityThroughWorkspace } from "@/hooks/useVisibilityThroughWorkspace";
import { usePromptLabels } from "@/store";
import { MediaItemStatus } from "@/types/media";

export const DeployContextProvider = ({ children }: { children: React.ReactNode }) => {
    const api = useApi();
    const currentWorkspace = useCurrentWorkspace();
    const promptLabels = usePromptLabels();

    // Use store hooks instead of local state
    const workflow = useCurrentWorkflow();
    const setWorkflow = useSetWorkflow();
    const apiKey = useCurrentWorkspaceApiKey() || "";
    const creatingRapidWorkflow = useIsWorkflowCreating();
    const setCreatingRapidWorkflow = useSetCreatingRapidWorkflow();
    const createRapidWorkflow = useCreateRapidWorkflow();
    const fetchCurrentWorkspaceApiKey = useFetchCurrentWorkspaceApiKey();
    const { track } = useAnalytics();
    const visibility = useVisibilityThroughWorkspace();
    const { mediaItems } = useMedia();

    // Keep only UI-specific state in context
    const [dataMode, setDataMode] = useState<DataMode>(DataMode.IMAGE);
    const [loading, setLoading] = useState<boolean>(false);

    const currentRapidData = useCurrentRapidData();
    const initializeModel = useInitializeModelFromRapidData();
    const clearModelState = useClearModelState();
    const user = useUser();

    // Fetch API key when workspace changes and no API key is available
    useEffect(() => {
        if (currentWorkspace?.id && !apiKey) {
            fetchCurrentWorkspaceApiKey(api);
        }
    }, [currentWorkspace?.id, apiKey, fetchCurrentWorkspaceApiKey, api]);

    // Track previous rapidData state to detect meaningful changes
    const prevRapidDataRef = useRef<{ id: string | null; modelChangesLength: number }>({
        id: null,
        modelChangesLength: 0
    });

    // Initialize model state from rapid data for metrics entry point
    // Only re-initialize when rapidData ID or modelChanges actually change
    useEffect(() => {
        if (!currentRapidData) {
            if (!workflow?.rapidDataId) {
                clearModelState();
                prevRapidDataRef.current = { id: null, modelChangesLength: 0 };
            }
            return;
        }

        if (!user?.uid) {
            return;
        }

        const currentId = currentRapidData.id;
        const currentModelChangesLength = currentRapidData.modelChanges?.length ?? 0;
        const prev = prevRapidDataRef.current;

        // Only initialize if ID changed or modelChanges array changed
        const hasIdChanged = currentId !== prev.id;
        const hasModelChangesChanged = currentModelChangesLength !== prev.modelChangesLength;

        if (hasIdChanged || hasModelChangesChanged) {
            initializeModel(currentRapidData, user.uid);
            prevRapidDataRef.current = {
                id: currentId,
                modelChangesLength: currentModelChangesLength
            };
        }
    }, [currentRapidData, user?.uid, workflow?.rapidDataId, initializeModel, clearModelState]);

    const modelStatus = useMemo(() => {
        if (!workflow || !currentRapidData) return ModelStatus.INITIALIZING;

        const isDeployed = !!currentRapidData?.lastExecutedAt;

        if (isDeployed) {
            return ModelStatus.DEPLOYED;
        }

        const allRfInstantModels =
            currentRapidData.models[AvailableRapidModelArchitectures.RF_INSTANT];
        const allRfDetrModels = currentRapidData.models[AvailableRapidModelArchitectures.RF_DETR];

        const latestRfInstantModel = allRfInstantModels[allRfInstantModels.length - 1];
        const latestRfDetrModel = allRfDetrModels[allRfDetrModels.length - 1];

        const latestRfInstantModelTrainStatus = latestRfInstantModel?.train?.status;
        const latestRfDetrModelTrainStatus = latestRfDetrModel?.train?.status;

        if (
            latestRfInstantModelTrainStatus === RapidTrainStatus.TRAINING ||
            latestRfDetrModelTrainStatus === RapidTrainStatus.TRAINING
        ) {
            return ModelStatus.TRAINING;
        }

        if (
            allRfInstantModels.length === 1 &&
            allRfDetrModels.length === 1 &&
            latestRfInstantModelTrainStatus === RapidTrainStatus.PENDING &&
            latestRfDetrModelTrainStatus === RapidTrainStatus.PENDING
        ) {
            return ModelStatus.INITIALIZING;
        }

        return ModelStatus.NOT_DEPLOYED;
    }, [workflow, currentRapidData]);

    // Wrapper function to match the expected interface
    const createRapidWorkflowDeploy = useCallback(
        async (props: Omit<CreateRapidWorkflowProps, "buildMetadata" | "exampleData">) => {
            try {
                track({
                    event: AnalyticsFlowEvent.CREATE_WORKFLOW_STARTED,
                    data: { task: props.task }
                });

                const exampleData = mediaItems
                    .filter(
                        (mediaItem) =>
                            mediaItem.status === MediaItemStatus.COMPLETED &&
                            mediaItem.uploadResult?.filename
                    )
                    .map((mediaItem) => ({
                        isVideo: mediaItem.isVideo,
                        filePath: mediaItem.uploadResult?.filename || mediaItem.id,
                        resizedFilePath: mediaItem.uploadResizedResult?.filename || null,
                        isSharable: visibility === "public"
                    }));
                if (exampleData.length === 0) {
                    throw new Error("Cannot create workflow without uploaded media items");
                }

                // Build metadata to track which inference type was used for each class
                const buildMetadata: BuildMetadata = { inferenceTypes: {} };
                promptLabels.forEach((label) => {
                    // If the label has diffs (user drew/edited boxes), use OWLv2
                    // Otherwise, use SAM3 (text-only prompting)
                    buildMetadata.inferenceTypes[label.name] = label.hasDiffs
                        ? BuildInferenceType.OWLV2
                        : BuildInferenceType.SAM3;
                });

                const { workflow, error } = await createRapidWorkflow(
                    { ...props, exampleData, buildMetadata },
                    api
                );

                if (error) {
                    throw error;
                }
                if (!workflow) {
                    throw new Error("Workflow creation failed: No workflow returned");
                } else {
                    track({
                        event: AnalyticsFlowEvent.WORKFLOW_CREATED,
                        data: { task: props.task, workflow }
                    });

                    return workflow;
                }
            } catch (error) {
                console.error("DeployProvider createRapidWorkflow error", error);
                throw error;
            }
        },
        [createRapidWorkflow, api, track, mediaItems, visibility, promptLabels]
    );

    const getWorkflowEndpoint = useCallback((workspaceUrl: string, workflowUrl: string) => {
        return projectSelector.workflowUrl(workspaceUrl, workflowUrl);
    }, []);

    const hasValidWorkflowCredentials = !!(workflow?.url && currentWorkspace?.url);

    const workflowCredentials = useMemo(() => {
        const workspaceUrl = currentWorkspace?.url || "your-workspace-url";
        const workflowUrl = workflow?.url || "your-workflow-url";

        return {
            workspaceUrl,
            apiKey: apiKey || "your-api-key",
            workflowUrl,
            baseUrl: getWorkflowEndpoint(workspaceUrl, workflowUrl),
            dataMode
        };
    }, [currentWorkspace?.url, workflow?.url, apiKey, getWorkflowEndpoint, dataMode]);

    const deployContextValue: DeployContextType = useMemo(() => {
        return {
            dataMode,
            setDataMode,
            modelStatus,
            workflow,
            setWorkflow,
            workflowCredentials,
            hasValidWorkflowCredentials,
            apiKey,
            loading,
            setLoading,
            creatingRapidWorkflow,
            setCreatingRapidWorkflow,
            createRapidWorkflowDeploy,
            getWorkflowEndpoint
        };
    }, [
        dataMode,
        setDataMode,
        modelStatus,
        workflow,
        setWorkflow,
        workflowCredentials,
        hasValidWorkflowCredentials,
        apiKey,
        loading,
        setLoading,
        creatingRapidWorkflow,
        setCreatingRapidWorkflow,
        createRapidWorkflowDeploy,
        getWorkflowEndpoint
    ]);

    return <DeployContext.Provider value={deployContextValue}>{children}</DeployContext.Provider>;
};
